import { z } from 'zod';
import {
  LibraryMetaSchema,
  LibraryMetaSourceSchema,
  LibraryMetaStatusSchema,
  LibraryMetaUniquenessSchema,
  type LibraryMeta,
} from '@/IsekaiRPG/shared/types';
import { ExplorationRoomTypeSchema } from './explorationShared';

const RoomTemplateLibraryMetaInputSchema = z.object({
  reusable: z.boolean().optional(),
  uniqueness: LibraryMetaUniquenessSchema.optional(),
  source: LibraryMetaSourceSchema.optional(),
  status: LibraryMetaStatusSchema.optional(),
  exposeToAi: z.boolean().optional(),
  tags: z.array(z.string()).optional(),
});

function normalizeRoomTemplateTags(tags: string[] | undefined): string[] {
  const seen = new Set<string>();
  const normalized: string[] = [];

  (tags || []).forEach(tag => {
    const nextTag = tag.trim();
    if (!nextTag) return;
    const lowerKey = nextTag.toLowerCase();
    if (seen.has(lowerKey)) return;
    seen.add(lowerKey);
    normalized.push(nextTag);
  });

  return normalized;
}

function resolveRoomTemplateLibraryMeta(libraryMeta?: Partial<LibraryMeta>): LibraryMeta {
  return LibraryMetaSchema.parse({
    reusable: true,
    uniqueness: 'generic',
    source: 'builtin',
    status: 'promoted',
    exposeToAi: true,
    ...libraryMeta,
    tags: normalizeRoomTemplateTags(libraryMeta?.tags),
  });
}

export const RoomTemplateSchema = z
  .object({
    id: z.string().min(1),
    roomType: ExplorationRoomTypeSchema,
    title: z.string().min(1),
    description: z.string().default(''),
    rewards: z.array(z.string()).default([]),
    minDifficulty: z.number().int().min(1).optional(),
    maxDifficulty: z.number().int().min(1).optional(),
    libraryMeta: RoomTemplateLibraryMetaInputSchema.optional(),
  })
  .transform(value => ({
    ...value,
    libraryMeta: resolveRoomTemplateLibraryMeta(value.libraryMeta),
  }));

export type RoomTemplate = z.infer<typeof RoomTemplateSchema>;
export type RoomTemplateInput = z.input<typeof RoomTemplateSchema>;

export function buildRoomTemplate(input: RoomTemplateInput): RoomTemplate {
  return RoomTemplateSchema.parse(input);
}

export function getRoomTemplateTags(template: Pick<RoomTemplate, 'libraryMeta'>): string[] {
  return normalizeRoomTemplateTags(template.libraryMeta.tags || []);
}

export function shouldExposeRoomTemplateToAi(template: Pick<RoomTemplate, 'libraryMeta'>): boolean {
  return (
    template.libraryMeta.reusable !== false &&
    template.libraryMeta.exposeToAi !== false &&
    template.libraryMeta.status !== 'disabled'
  );
}
